import '@place/ui/css/reset.css'
import '@place/ui/css/global.css'
import '@place/ui/css/style.css'
import '@local/assets/scss/global.scss'
import '@local/assets/scss/font.scss'
import '@local/assets/scss/media_viewer.scss'
import '@egjs/flicking/dist/flicking.css'
import '@egjs/flicking-plugins/dist/pagination.css'
import 'dayjs/locale/ko'
import '../components/FlickingArea/FlickingArea.global.scss'

import { ReactNode } from 'react'

import { ApolloProvider } from '@apollo/client'
import { AnchorContextProvider } from '@place/ui/contexts'
import App, { AppContext, AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { Helmet } from 'react-helmet'

import { webRoutes } from '@local/utils'

import Anchor from '@web/components/Anchor'
import ErrorBoundary from '@web/components/ErrorBoundary'
import { useEventPopup } from '@web/components/EventPopup/useEventPopup'
import useGlobalAfterHydration from '@web/hooks/useGlobalAfterHydration'
import useGlobalAuthCookieChange from '@web/hooks/useGlobalAuthCookieChange'
import useGlobalBodyClass from '@web/hooks/useGlobalBodyClass'
import { useGlobalCacheAsync } from '@web/hooks/useGlobalCacheAsync'
import { useGlobalDetermineDarkMode } from '@web/hooks/useGlobalDetermineDarkMode'
import { useGlobalFirstPage } from '@web/hooks/useGlobalFirstPage'
import useGlobalOnline from '@web/hooks/useGlobalOnline'
import useGlobalPreviousRoute from '@web/hooks/useGlobalPreviousRoute'
import { GlobalStaticStateContextProvider, ServerProps } from '@web/hooks/useGlobalStaticState'
import { useCloseSaveWidget } from '@web/hooks/useMapWidget'
import { NavigationContextProvider } from '@web/hooks/useNavigation'
import { SSRDateTimeVar } from '@web/hooks/useNow'
import { useGlobalPageViewLogging } from '@web/hooks/usePageViewLogging'
import { useApollo } from '@web/libs/apollo/client'
import { useGlobalMyplaceDevtools } from '@web/libs/dev/MyplaceDevtools/useGlobalMyplaceDevtools'
// import { initSentry } from '@web/libs/sentryInit'
import { setSentryTags } from '@web/libs/setSentryTags'
import { setLanguage } from '@web/libs/translate/translate.state'
import MissionRewardPopup from '@web/pages/mission/components/MissionRewardPopup/MissionRewardPopup'
import MyClickable from '@web/pages/v5/components/MyClickable'
import { DefaultServerSideProps } from '@web/types/app'
import { ClickableContextProvider } from '@web/ui/components/Clickable/context'

/**
 * @see https://oss.navercorp.com/place/local/issues/19953
 * 25년 4월 4일 Sentry Saas 사용 기간 종료
 * 다시 사용가능해질 때 주석 해제해서 사용
 */
// initSentry()

const GlobalModalPortal = dynamic(() => import('@web/components/Modal/GlobalModal/GlobalModalPortal'))
const GlobalToastPortal = dynamic(() => import('@web/components/Toast/GlobalToastPortal'))

const GlobalHooks = ({ children }: { children: ReactNode }) => {
  useGlobalDetermineDarkMode()
  useGlobalAfterHydration()
  useGlobalBodyClass()

  useGlobalAuthCookieChange()
  useGlobalOnline()
  useGlobalPageViewLogging()
  useGlobalFirstPage()
  useGlobalCacheAsync()
  useEventPopup()
  useGlobalMyplaceDevtools()
  // useGlobalDebugging()

  return <>{children}</>
}

const federationPages = ['/v5', '/home/federation', webRoutes.TIMELINE, webRoutes.REVIEW_NEW, webRoutes.REVIEW_EDIT]
const shouldUseFederationClient = (pathname: string) =>
  federationPages.some(path => pathname.startsWith(path)) ||
  pathname === webRoutes.HOME ||
  pathname === webRoutes.SETTING

const WebApp = ({ Component, pageProps }: AppProps<DefaultServerSideProps & ServerProps>) => {
  const router = useRouter()
  const apolloClient = useApollo(pageProps.initialState, shouldUseFederationClient(router.pathname))
  useGlobalPreviousRoute(pageProps)
  useCloseSaveWidget()

  setSentryTags(pageProps.userIdno)
  setLanguage(pageProps.acceptLanguage)
  SSRDateTimeVar(pageProps.SSRDateTime)

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no" />
      </Helmet>
      <ErrorBoundary serverProps={pageProps} hasErrorFromPage={pageProps.hasError}>
        <GlobalStaticStateContextProvider serverProps={pageProps}>
          <ApolloProvider client={apolloClient}>
            <NavigationContextProvider>
              <ClickableContextProvider clickable={MyClickable}>
                <AnchorContextProvider anchor={Anchor}>
                  <GlobalHooks>
                    <GlobalModalPortal />
                    <GlobalToastPortal />
                    <Component {...pageProps} />
                    <MissionRewardPopup />
                  </GlobalHooks>
                </AnchorContextProvider>
              </ClickableContextProvider>
            </NavigationContextProvider>
          </ApolloProvider>
        </GlobalStaticStateContextProvider>
      </ErrorBoundary>
    </>
  )
}

WebApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext)

  const acceptLanguage = appContext.ctx.req?.headers['accept-language']
  const referrer = appContext.ctx.req?.headers['referer']
  const userAgent = appContext.ctx.req?.headers['user-agent']
  const userIdno = appContext.ctx.req?.headers['nid_id_no']

  const { resetServerContext } = await import('react-beautiful-dnd')
  resetServerContext()

  return {
    ...appProps,
    pageProps: {
      acceptLanguage,
      referrer,
      userAgent,
      userIdno,
      SSRDateTime: new Date().toISOString(),
      path: appContext.router.asPath,
    } as ServerProps,
  }
}

export default WebApp
